/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react'

import {
  COLUMN_NAMES as DUEOUT_COLUMN_NAMES,
  DueOut,
  DueOutFetchArgs,
  deleteDueout,
  getDueOutsViews,
} from '../../assets/ts/data/dueout/dueout'
import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  Inquiry,
  InquiryFetchArgs,
  deleteInquiry,
  getInquiryViews,
} from '../../assets/ts/data/inquiry/inquiry'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import { scrollToTop } from '../../assets/ts/general/general'
import Alert from '../../components/alert/alert'
import ExportToCSVButton from '../../components/buttons/export-to-csv-btn'
import Filters from '../../components/filters/filters'
import KPIs from '../../components/kpi/kpis'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import MyDueoutsTable from '../../components/table/my-dueouts-table'
import MyInquiriesTable from '../../components/table/my-inquiries-table'
import Tabs from '../../components/tabs/tabs'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutDistinctValues from '../../hooks/dueout/use-dueout-distinct-values'
import useDueoutViews from '../../hooks/dueout/use-dueout-view'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import useInquiryDistinctValues from '../../hooks/inquiry/use-inquiry-distinct-values'
import useInquiryViews from '../../hooks/inquiry/use-inquiry-views'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import PageHeader from '../../layouts/header/page-header'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { SelectionContext } from '../../store/selection-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
  DUEOUT_INITIAL_FILTER,
  INQUIRY_INITIAL_FILTER,
} from '../../utils/constants/global-constants'

const MyInquiriesList = () => {
  const selection = useContext(SelectionContext)
  const { me } = useContext(UserContext)
  const { currentPage, setCurrentPage, setPreviousPage } = useContext(PreviousPageContext)
  const INQUIRIES_TAB = 0
  const DUEOUTS_TAB = 1

  const [inquiriesPageNum, setInquiriesPageNum] = useState(0)
  const [dueoutsPageNum, setDueoutsPageNum] = useState(0)
  const [resetFilters, setResetFilters] = useState(true)
  const [showSavedInquiries, setShowSavedInquiries] = useState(false)
  const [showSavedDueouts, setShowSavedDueouts] = useState(false)
  const [exportFetchIsBusy, setExportFetchIsBusy] = useState(false)
  const [tabSelected, setTabSelected] = useState<0 | 1>(INQUIRIES_TAB)
  const initialKpiFilters: DueOutFetchArgs | InquiryFetchArgs = {
    activeFilter: null,
    customerActionRequiredFilter: false,
    dueDateLessThanDateIgnoreTime: undefined,
    isArchivedFilter: false,
    isViewApplied: true,
    myDueouts: me.userId,
    myInquiries: me.userId,
    savedFilter: false,
    statusCodesFilter: undefined,
  }
  const [inquiryFilter, setInquiryFilter] = useState<InquiryFetchArgs>({
    ...INQUIRY_INITIAL_FILTER,
    ...initialKpiFilters,
  })
  const [dueoutFilter, setDueoutFilter] = useState<DueOutFetchArgs>({
    ...DUEOUT_INITIAL_FILTER,
    ...initialKpiFilters,
  })

  const inquiryPaging: InquiryFetchArgs = {
    pageNumber: inquiriesPageNum,
  }
  const dueOutPaging: DueOutFetchArgs = {
    pageNumber: dueoutsPageNum,
  }
  const { inquiries, inquiriesAreLoading } = useInquiryViews({
    ...inquiryFilter,
    ...inquiryPaging,
  })
  const [inquirySavedFilter, setInquirySavedFilter] = useState<InquiryFetchArgs>({
    ...initialKpiFilters,
    isSavedInquiryDeleted: false,
    savedFilter: true,
  })
  const [dueoutSavedFilter, setDueoutSavedFilter] = useState<DueOutFetchArgs>({
    ...initialKpiFilters,
    isSavedDueoutDeleted: false,
    savedFilter: true,
  })
  const { inquiries: savedInquires } = useInquiryViews({
    ...inquirySavedFilter,
  })
  const { dueouts, dueoutsAreLoading } = useDueoutViews({
    ...dueoutFilter,
    ...dueOutPaging,
  })
  const { dueouts: savedDueouts } = useDueoutViews({
    ...dueoutSavedFilter,
  })
  const { inquiryCountIsLoading, inquiryCountValue } = useInquiryCount({
    ...inquiryFilter,
  })
  const { dueOutCountIsLoading, dueOutCountValue } = useDueOutCount({
    ...dueoutFilter,
  })
  const accumulatedInquiries = usePageAccumulator(inquiriesPageNum, inquiries)
  const accumulatedDueOuts = usePageAccumulator(dueoutsPageNum, dueouts)
  const handleBottomInquiryScroll = () => {
    if (inquiryCountValue > accumulatedInquiries.length) {
      setInquiriesPageNum((num) => num + 1)
    }
  }
  const handleBottomDueoutScroll = () => {
    if (dueOutCountValue > accumulatedDueOuts.length) {
      setDueoutsPageNum((num) => num + 1)
    }
  }

  const busy =
    inquiriesAreLoading ||
    inquiryCountIsLoading ||
    dueoutsAreLoading ||
    dueOutCountIsLoading ||
    exportFetchIsBusy
  const handleTabSelection = (tabNumber: 0 | 1) => {
    setTabSelected(tabNumber)
    setResetFilters(!resetFilters)
    setShowSavedInquiries(false)
    handleClearAll()
  }
  const { inquiryDistinctValues } = useInquiryDistinctValues({
    assignedComponentCodeFilter: inquiryFilter.assignedComponentCodeFilter,
    categoryCodeFilter: inquiryFilter.categoryCodeFilter as number,
    currentlyServicingByFilter: inquiryFilter.currentlyServicingByFilter,
    distinctFiltersToQuery: [
      INQUIRY_COLUMN_NAMES.FY,
      INQUIRY_COLUMN_NAMES.AssignedComponentCode,
      INQUIRY_COLUMN_NAMES.ServicingMAJCOMCode,
      INQUIRY_COLUMN_NAMES.CurrentlyServicingBy,
      INQUIRY_COLUMN_NAMES.ServicingOrganization,
      INQUIRY_COLUMN_NAMES.SectionCode,
      INQUIRY_COLUMN_NAMES.CategoryCode,
      INQUIRY_COLUMN_NAMES.SubCategoryCode,
    ],
    isArchivedFilter: false,
    isViewApplied: true,
    myInquiries: me.userId,
    sectionCodeFilter: inquiryFilter.sectionCodeFilter,
    servicingMAJCOMCodeFilter: inquiryFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: inquiryFilter.servicingOrganizationFilter,
    subCategoryCodeFilter: inquiryFilter.subCategoryFilter as number,
    yearFilter: inquiryFilter.yearFilter,
  })
  const { dueOutDistinctValues } = useDueOutDistinctValues({
    assignedComponentCodeFilter: dueoutFilter.assignedComponentCodeFilter,
    categoryCodeFilter: dueoutFilter.categoryCodeFilter as number,
    distinctFiltersToQuery: [
      DUEOUT_COLUMN_NAMES.FY,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.ServicingMAJCOMCode,
      DUEOUT_COLUMN_NAMES.ServicingOrganization,
      DUEOUT_COLUMN_NAMES.ServicingBy,
      DUEOUT_COLUMN_NAMES.SectionCode,
      DUEOUT_COLUMN_NAMES.CategoryCode,
      DUEOUT_COLUMN_NAMES.SubCategoryCode,
      DUEOUT_COLUMN_NAMES.Priority,
    ],
    isArchivedFilter: false,
    isViewApplied: true,
    myDueouts: me.userId,
    sectionCodeFilter: dueoutFilter.sectionCodeFilter,
    servicingByFilter: dueoutFilter.servicingByFilter,
    servicingMAJCOMCodeFilter: dueoutFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: dueoutFilter.servicingOrganizationFilter,
    subCategoryCodeFilter: dueoutFilter.subCategoryCodeFilter as number,
    yearFilter: dueoutFilter.yearFilter,
  })
  const resetFilterOptions = (priorArgs, order, updatedArgs) => ({
    ...priorArgs,
    ...initialKpiFilters,
    assignedComponentCodeFilter:
      order && order >= 2 ? priorArgs.assignedComponentCodeFilter : ALL_SELECT_VALUE,
    categoryCodeFilter: order && order >= 9 ? priorArgs.categoryCodeFilter : ALL_SELECT_VALUE,
    currentlyServicingByFilter:
      order && order >= 4 ? priorArgs.currentlyServicingByFilter : ALL_SELECT_VALUE_STRING,
    searchFilter: '',
    sectionCodeFilter: order && order >= 8 ? priorArgs.sectionCodeFilter : ALL_SELECT_VALUE,
    servicingByFilter: order && order >= 4 ? priorArgs.servicingByFilter : ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter:
      order && order >= 3 ? priorArgs.servicingMAJCOMCodeFilter : ALL_SELECT_VALUE,
    servicingOrganizationFilter:
      order && order >= 5 ? priorArgs.servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    subCategoryCodeFilter:
      order && order >= 10 ? priorArgs.subCategoryCodeFilter : ALL_SELECT_VALUE,
    yearFilter: order && order >= 1 ? priorArgs.yearFilter : ALL_SELECT_VALUE,
    ...updatedArgs,
  })

  const updateInquiryFilter = (updatedArgs, order) => {
    scrollToTop()
    selection.reset()
    setShowSavedInquiries(false)
    setInquiriesPageNum(0)
    setInquiryFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }
  const updateInquiryKpiFilter = (updatedArgs, title) => {
    scrollToTop()
    selection.reset()
    setInquiriesPageNum(0)
    if (title === 'Saved') setShowSavedInquiries(true)
    else {
      setShowSavedInquiries(false)
      setInquiryFilter((priorArgs) => ({
        ...priorArgs,
        ...initialKpiFilters,
        ...updatedArgs,
      }))
    }
  }

  const updateDueoutFilter = (updatedArgs, order) => {
    scrollToTop()
    selection.reset()
    setShowSavedDueouts(false)
    setDueoutsPageNum(0)
    setDueoutFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }
  const updateDueoutKpiFilter = (updatedArgs, title) => {
    scrollToTop()
    selection.reset()
    setDueoutsPageNum(0)
    if (title === 'Saved') setShowSavedDueouts(true)
    else {
      setShowSavedDueouts(false)
      setDueoutFilter((priorArgs) => ({
        ...priorArgs,
        ...initialKpiFilters,
        ...updatedArgs,
      }))
    }
  }
  const handleClearAll = () => {
    setInquiryFilter({
      ...INQUIRY_INITIAL_FILTER,
      ...initialKpiFilters,
      customerActionRequiredFilter: false,
    })
    setDueoutFilter({
      ...DUEOUT_INITIAL_FILTER,
      ...initialKpiFilters,
    })
    setShowSavedDueouts(false)
    setShowSavedInquiries(false)
    setInquiriesPageNum(0)
    setDueoutsPageNum(0)
    selection.reset()
  }
  const getSearch = (event: { target: { value: string } }): void => {
    setInquiriesPageNum(0)
    const searchWord: string = event.target.value.toLocaleLowerCase()
    const updateFilter = (filterSetter: any, filter: DueOutFetchArgs | InquiryFetchArgs) => {
      filterSetter({ ...filter, searchFilter: searchWord })
    }

    switch (tabSelected) {
      case 0:
        updateFilter(setInquiryFilter, inquiryFilter)
        break
      case 1:
        updateFilter(setDueoutFilter, dueoutFilter)
        break
      default:
        break
    }
  }

  async function getAllSelectedRecords(): Promise<Inquiry[]> {
    let visibleSelectedRecords
    if (showSavedInquiries) {
      visibleSelectedRecords = savedInquires.filter((d) =>
        selection.keyIsSelected(d.InquiryGUID ?? ''))
    } else {
     visibleSelectedRecords = accumulatedInquiries.filter((d) =>
      selection.keyIsSelected(d.InquiryGUID ?? '')
    )}
    const unloadedSelectedRecords = await getAllUnloadedSelectedRecords()

    const allSelectedRecords = [...visibleSelectedRecords, ...unloadedSelectedRecords]
    return allSelectedRecords
  }

  async function getAllDueoutSelectedRecords(): Promise<DueOut[]> {
    let visibleSelectedRecords
    if (showSavedDueouts) {
      visibleSelectedRecords = savedDueouts.filter((d) =>
        selection.keyIsSelected(d.DueOutGUID ?? '')
      )
    } else {
     visibleSelectedRecords = accumulatedDueOuts.filter((d) =>
      selection.keyIsSelected(d.DueOutGUID ?? '')
    )}

    const unloadedSelectedRecords = await getAllDueoutsUnloadedSelectedRecords()

    const allSelectedRecords = [...visibleSelectedRecords, ...unloadedSelectedRecords]

    return allSelectedRecords
  }
  async function getAllUnloadedSelectedRecords(): Promise<Inquiry[]> {
    if (selection.unloadedRecordsAreSelected) {
      setExportFetchIsBusy(true)
      const items = await getInquiryViews({
        ...inquiryFilter,
        onePageOnly: false,
        pageNumber: (inquiryPaging.pageNumber ?? 0) + 1,
      })
      setExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }
  async function getAllDueoutsUnloadedSelectedRecords(): Promise<DueOut[]> {
    if (selection.unloadedRecordsAreSelected) {
      setExportFetchIsBusy(true)
      const items = await getDueOutsViews({
        ...dueoutFilter,
        onePageOnly: false,
        pageNumber: (dueOutPaging.pageNumber ?? 0) + 1,
      })
      setExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }

  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false)
  const [selectedItem, setselectedItem] = useState<string>()
  const deleteItems = async (selectedItem: string) => {
    setselectedItem(selectedItem)
    setShowConfirmationAlert(true)
  }
  const deleteConfirmationAlert = (button: string): void => {
    if (button === 'OK') {
      tabSelected === INQUIRIES_TAB ? deleteConformedInquiries() : deleteConformedDueouts()
    }
    setShowConfirmationAlert(false)
  }
  const deleteConformedInquiries = async () => {
    const delinquiry = savedInquires.find((item) => (item.InquiryGUID as string) === selectedItem)
    if (delinquiry) {
      await deleteInquiry(delinquiry.eKey, delinquiry.eTag)
    }
    setInquirySavedFilter((prev) => ({
      ...prev,
      isSavedInquiryDeleted: !prev.isSavedInquiryDeleted,
    }))
  }
  const deleteConformedDueouts = async () => {
    const delDueout = savedDueouts.find((item) => (item.DueOutGUID as string) === selectedItem)
    if (delDueout) {
      await deleteDueout(delDueout.eKey, delDueout.eTag)
    }
    setDueoutSavedFilter((prev) => ({
      ...prev,
      isSavedDueoutDeleted: !prev.isSavedDueoutDeleted,
    }))
  }
 
  const ExportToCSVButtonVisibility = (): boolean => {
    if (tabSelected === INQUIRIES_TAB) {
      return showSavedInquiries ? savedInquires.length > 0 : inquiryCountValue > 0
    } else {
      return showSavedDueouts ? savedDueouts.length > 0 : dueOutCountValue > 0
    }
  }

  const tableItems = [
    {
      content: (
        <MyInquiriesTable
          csvTitle={'MY INQUIRIES'}
          deleteItems={deleteItems}
          inquiries={showSavedInquiries ? savedInquires : accumulatedInquiries}
          isSavedInquiry={showSavedInquiries}
          onBottomScrollReached={handleBottomInquiryScroll}
        />
      ),
      count: twoDigits(showSavedInquiries ? savedInquires.length : inquiryCountValue),
      isActive: tabSelected === INQUIRIES_TAB,
      tabTitle: 'MY INQUIRIES',
    },
    {
      content: (
        <MyDueoutsTable
          deleteItems={deleteItems}
          dueOuts={showSavedDueouts ? savedDueouts : accumulatedDueOuts}
          onBottomScrollReached={handleBottomDueoutScroll}
          title='Total Due Outs'
        />
      ),
      count: twoDigits(showSavedDueouts ? savedDueouts.length : dueOutCountValue),
      isActive: tabSelected === DUEOUTS_TAB,
      tabTitle: 'DUE OUTS',
    },
  ]

  useEffect(() => {
    if (tabSelected === INQUIRIES_TAB) {
      showSavedInquiries && selection.mergeWithAllLoadedRecordKeys(savedInquires.map((i) => i.InquiryGUID ?? ''))
      !showSavedInquiries && selection.mergeWithAllLoadedRecordKeys(inquiries.map((i) => i.InquiryGUID ?? ''))
    }
  }, [inquiries, showSavedInquiries, tabSelected])

  useEffect(() => {
    if (tabSelected === DUEOUTS_TAB) {
      showSavedDueouts && selection.mergeWithAllLoadedRecordKeys(savedDueouts.map((i) => i.DueOutGUID ?? ''))
      !showSavedDueouts && selection.mergeWithAllLoadedRecordKeys(dueouts.map((i) => i.DueOutGUID ?? ''))
    }
  }, [dueouts, showSavedDueouts, tabSelected])
  useEffect(() => {
    scrollToTop()
  }, [])

  useEffect(() => {
    // This effect runs whenever the location changes (when the user navigates to a different route)
    if (currentPage !== location.pathname) {
      // User has navigated to a new page, so update the previous page
      setPreviousPage(currentPage) // Store the previous page
      setCurrentPage(location.pathname) // Update the current page
    }
  }, [location, currentPage]) // Runs whenever location or currentPage changes

  return (
    <>
      <div id='scroll'></div>
      {dueoutFilter.searchFilter === '' && inquiryFilter.searchFilter === '' && busy && (
        <PageOverlayBusy />
      )}
      {showConfirmationAlert && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={'Are you sure you want to delete the selected item?'}
          onClick={deleteConfirmationAlert}
          yes='OK'
        />
      )}

      <PageHeader
        extras={<div></div>}
        icon='icon-dueouts font-14 me-1 color-primary'
        name='My Inquiries '
      />
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 col-lg listview-table'>
            <div className='shadow card w-100 h-100'>
              <div className='card-body'>
                <Tabs
                  anchorclass='title-color2 font-12 poppins-semibold'
                  badge='badge font-12 border-radius3 px-2 py-1'
                  className='title-color2 lg'
                  isButtons={
                    ExportToCSVButtonVisibility() ? (
                      <ExportToCSVButton
                        fetchDataFunc={
                          tabSelected === INQUIRIES_TAB
                            ? getAllSelectedRecords
                            : getAllDueoutSelectedRecords
                        }
                        pageType={
                          tabSelected === INQUIRIES_TAB
                            ? 'MyInquiries/Inquiries'
                            : 'MyInquiries/Dueouts'
                        }
                      />
                    ) : (
                      ''
                    )
                  }
                  items={tableItems}
                  onTabSelected={handleTabSelection}
                  tablistclassName='overflow-auto'
                />
              </div>
            </div>
          </div>
          <div className='col-sm-12 listview-filters order-first order-lg-0'>
            <div className='w-100 h-100 whitebg bordered1'>
              <div className='filter-section mb-3'>
                <Filters
                  availableFilterValues={
                    tabSelected === INQUIRIES_TAB
                      ? inquiryDistinctValues[0]
                      : dueOutDistinctValues[0]
                  }
                  filter={tabSelected === INQUIRIES_TAB ? inquiryFilter : dueoutFilter}
                  getSearch={getSearch}
                  onClearAll={handleClearAll}
                  onUpdateFilter={
                    tabSelected === INQUIRIES_TAB ? updateInquiryFilter : updateDueoutFilter
                  }
                  pageType={
                    tabSelected === INQUIRIES_TAB ? `MyInquiries/Inquiries` : `MyInquiries/Dueouts`
                  }
                  resetFilters={resetFilters}
                />
              </div>
              <div className='kpis-section mb-3'>
                <KPIs
                  filter={tabSelected === INQUIRIES_TAB ? inquiryFilter : dueoutFilter}
                  initialKpiFilters={initialKpiFilters}
                  isSavedDueoutDeleted={dueoutSavedFilter.isSavedDueoutDeleted}
                  isSavedInquiryDeleted={inquirySavedFilter.isSavedInquiryDeleted}
                  pageType={tabSelected === INQUIRIES_TAB ? 'MY INQUIRIES' : 'MY DUEOUTS'}
                  updateKpiFilter={
                    tabSelected === INQUIRIES_TAB ? updateInquiryKpiFilter : updateDueoutKpiFilter
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default MyInquiriesList
